import { CloseButton, Flex, Text } from '@chakra-ui/react'
import React, { useMemo } from 'react'

import { textStyleKeys } from '@mangovoice/design-system/textStyles'
import { PhoneNumber } from '@mangovoice/types'

import { Tooltip } from './ui/Tooltip'

const MAX_PILL_CHARACTERS = 15

interface PhoneNumberPillsProps {
  selectedPhoneNumbers: PhoneNumber[]
  onRemovePhone: (phone: PhoneNumber) => void
  onClearAll: () => void
  maxVisiblePills?: number
}

const truncateLabel = (label: string) => {
  if (label.length <= MAX_PILL_CHARACTERS) return label
  return label.slice(0, MAX_PILL_CHARACTERS) + '...'
}

const IndividualPill = ({
  phone,
  onRemove,
}: {
  phone: PhoneNumber
  onRemove: () => void
}) => {
  const displayLabel = phone.name || phone.number_for_display || phone.number
  const truncatedLabel = truncateLabel(displayLabel)
  const shouldShowTooltip = displayLabel !== truncatedLabel

  const pillContent = (
    <Flex
      css={{
        height: '1.75rem',
        px: '0.75rem',
        py: '0.375rem',
        bg: 'brand.subtle',
        border: '1px solid',
        borderColor: 'brand.solid',
        borderRadius: '1rem',
        gap: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <Text
        textStyle={textStyleKeys.Body2}
        css={{
          color: 'brand.solid',
          fontWeight: 400,
          whiteSpace: 'nowrap',
        }}
      >
        {truncatedLabel}
      </Text>
      <CloseButton
        onClick={onRemove}
        size="xs"
        css={{
          color: 'brand.solid',
          width: '1rem',
          height: '1rem',
          minW: '1rem',
          borderRadius: '0.125rem',
          _hover: {
            bg: 'brand.muted',
            color: 'brand.solid',
          },
        }}
      />
    </Flex>
  )

  if (!shouldShowTooltip) {
    return pillContent
  }

  return (
    <Tooltip
      content={displayLabel}
      showArrow
      positioning={{ placement: 'top' }}
      contentProps={{
        bg: 'fg.subtle',
        color: 'brand.contrast',
        px: '0.75rem',
        py: '0.5rem',
        borderRadius: '0.25rem',
        fontSize: '0.875rem',
      }}
    >
      {pillContent}
    </Tooltip>
  )
}

const SummaryPill = ({
  count,
  tooltipContent,
  onClearAll,
}: {
  count: number
  tooltipContent: string
  onClearAll: () => void
}) => {
  return (
    <Tooltip
      content={tooltipContent}
      showArrow
      positioning={{ placement: 'top' }}
      contentProps={{
        bg: 'fg.subtle',
        color: 'brand.contrast',
        px: '0.75rem',
        py: '0.5rem',
        borderRadius: '0.25rem',
        fontSize: '0.875rem',
        maxW: '20rem',
      }}
    >
      <Flex
        css={{
          height: '1.75rem',
          px: '0.75rem',
          py: '0.375rem',
          bg: 'brand.subtle',
          border: '1px solid',
          borderColor: 'brand.solid',
          borderRadius: '1rem',
          gap: '0.25rem',
          display: 'flex',
          alignItems: 'center',
          cursor: 'default',
          flexShrink: 0,
        }}
      >
        <Text
          textStyle={textStyleKeys.Body2}
          css={{
            color: 'brand.solid',
            fontWeight: 400,
            whiteSpace: 'nowrap',
          }}
        >
          {count} numbers selected
        </Text>
        <CloseButton
          onClick={onClearAll}
          size="xs"
          css={{
            color: 'brand.solid',
            width: '1rem',
            height: '1rem',
            minW: '1rem',
            borderRadius: '0.125rem',
            _hover: {
              bg: 'brand.muted',
              color: 'brand.solid',
            },
          }}
        />
      </Flex>
    </Tooltip>
  )
}

export const PhoneNumberPills = ({
  selectedPhoneNumbers,
  onRemovePhone,
  onClearAll,
  maxVisiblePills = 2,
}: PhoneNumberPillsProps) => {
  const shouldShowSummaryPill = selectedPhoneNumbers.length > maxVisiblePills

  const summaryTooltipContent = useMemo(() => {
    return selectedPhoneNumbers
      .map((phone) => phone.name || phone.number_for_display || phone.number)
      .join(', ')
  }, [selectedPhoneNumbers])

  if (selectedPhoneNumbers.length === 0) {
    return null
  }

  return (
    <>
      {shouldShowSummaryPill ? (
        <SummaryPill
          count={selectedPhoneNumbers.length}
          tooltipContent={summaryTooltipContent}
          onClearAll={onClearAll}
        />
      ) : (
        <>
          {selectedPhoneNumbers.map((phone) => (
            <IndividualPill
              key={phone.id}
              phone={phone}
              onRemove={() => onRemovePhone(phone)}
            />
          ))}
        </>
      )}
    </>
  )
}
